import React, {useState, useEffect, useRef, useCallback} from 'react';
import {
  Send,
  Bot,
  User,
  Trash2,
  Copy,
  X,
  Minus,
  Phone,
  Mail,
  Headphones,
  PlusCircle,
  Smile,
  Loader,
  Image,
} from 'lucide-react';
import TextsmsOutlinedIcon from '@mui/icons-material/TextsmsOutlined';
import Fab from '@mui/material/Fab';
import {
  Button,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  InputBase,
  TextField,
  Tooltip,
  Tabs, Tab, Grid, Dialog, Alert, DialogContent,
  DialogActions, Snackbar,
} from '@mui/material';
import {styled} from '@mui/material/styles';
import ReactMarkdown from 'react-markdown';
import removeMarkdown from 'remove-markdown';
import {CopyToClipboard} from 'react-copy-to-clipboard';
import UserService from "@/services/auth/UserService";
import SupportMenuItem from "@/parts/Header/components/SupportMenuItem";
import {useTranslation} from "react-i18next";
import {Refresh} from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import remarkGfm from 'remark-gfm';
import {MessageSquare} from 'lucide-react';
import CloseIcon from '@mui/icons-material/Close';
import {useSnackbar} from "notistack";


interface Message {
  content: string;
  sender: 'user' | 'bot';
  timestamp: Date;
  isLoading?: boolean;
  imageData?: string;
  displayName?: string;
  participantRole?: string;
}

const LoadingContainer = styled('div')({
  display: 'flex',
  gap: '4px',
  padding: '12px',
  backgroundColor: '#f5f5f5',
  borderRadius: '10px'
});

interface DotProps {
  delay: number;
}

const Dot = styled('div')<DotProps>(({delay}) => ({
  width: '8px',
  height: '8px',
  backgroundColor: '#9e9e9e',
  borderRadius: '50%',
  animation: `bounce 0.6s infinite ${delay}ms`,
  '@keyframes bounce': {
    '0%, 100%': {
      transform: 'translateY(0)'
    },
    '50%': {
      transform: 'translateY(-5px)'
    }
  }
}));


function ChatBot() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [isWidgetOpen, setIsWidgetOpen] = useState(false);
  const [hasPendingBotResponse, setHasPendingBotResponse] = useState(false);
  const [contactId, setContactId] = useState<string | null>(null);
  const [participantToken, setParticipantToken] = useState<string | null>(null);
  const [connectionToken, setConnectionToken] = useState<string | null>(null);
  const [wsUrl, setWsUrl] = useState<string | null>(null);
  const [chatBotToken, setChatBotToken] = useState<string>('');
  const [chatBotEmail, setChatBotEmail] = useState<string>('');
  const [chatBotDisplayName, setChatBotDisplayName] = useState<string>('');
  const [activeTab, setActiveTab] = useState("suggested");
  const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [initialLoadingSuggestions, setInitialLoadingSuggestions] = useState(true);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [userInfo, setUserInfo] = useState<any>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [newStartChat, setNewStartChat] = useState<boolean>(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [shouldEndChat, setShouldEndChat] = useState(false);
  const {t} = useTranslation();
  const [boostSessionId, setBoostSessionId] = useState<string | null>(null);
  const [isBoostMode, setIsBoostMode] = useState(true);
  const [chatHistories, setChatHistories] = useState<{ id: string; title: string; messages: any[]; timestamp: string; pinned?: boolean }[]>([]);
  const [pendingImageKey, setPendingImageKey] = useState<string | null>(null);
  const [pendingImageUrl, setPendingImageUrl] = useState<string | null>(null);
  const [pendingSuggestions, setPendingSuggestions] = useState<string[]>([]);
  const [allModels, setAllModels] = useState<Record<string, string[]>>({});
  const [isHumanMode, setIsHumanMode] = useState(false);

  const chatBottomRef = useRef<HTMLDivElement>(null);
  const notificationSoundRef = useRef<HTMLAudioElement>(new Audio('/sounds/pop-up-bubble-gfx-sounds-1-00-00.mp3'));
  const wsRef = useRef<WebSocket | null>(null);
  const humanWsRef = useRef<WebSocket | null>(null);
  const [callState, setCallState] = useState<'idle' | 'calling' | 'ringing' | 'active'>('idle');
  const [callDuration, setCallDuration] = useState(0);
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const callTimerRef = useRef<NodeJS.Timeout | null>(null);
  const callTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const humanSessionIdRef = useRef<string | null>(null);
  const pendingCandidatesRef = useRef<RTCIceCandidate[]>([]);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loadingChatHistory, setLoadingChatHistory] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [chatHistoryPage, setChatHistoryPage] = useState(1);
  const [chatHistoryTotal, setChatHistoryTotal] = useState(0);
  const [loadingSession, setLoadingSession] = useState<string | null>(null);
  const [selectedQA, setSelectedQA] = useState<{ question: string; answer: string } | null>(null);
  const [suggestedQAData, setSuggestedQAData] = useState<{ question: string; answer: string }[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const deletedIdsRef = useRef<Set<string>>(new Set());
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idleTimeoutRef = useRef<number | null>(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackResolved, setFeedbackResolved] = useState<'yes' | 'no' | null>(null);
  const [feedbackIssues, setFeedbackIssues] = useState<string[]>([]);
  const [feedbackText, setFeedbackText] = useState('');
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const {enqueueSnackbar} = useSnackbar()

  const initializeChat = useCallback(async (token: string, email: string) => {
    setIsChatLoading(true);
    try {
      // const response = await fetch('https://armaturachatbot.minervaiotstaging.com/api/ngteco/start-chat', {
      const response = await fetch('https://ai.minervaiot.com/api/ngteco/start-chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          "Authorization": 'Bearer ' + token,
          "X-User-ID": email
        },
      });

      const data = await response.json();
      const {contactId, participantToken, websocketUrl, connectionToken} = data;

      if (!contactId || !participantToken || !websocketUrl) {
        setIsChatLoading(false);
        return;
      }

      setContactId(contactId);
      setParticipantToken(participantToken);
      setWsUrl(websocketUrl);
      setConnectionToken(connectionToken);

      const ws = new WebSocket(websocketUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        ws.send(
          JSON.stringify({
            topic: 'aws/subscribe',
            content: {topics: ['aws/chat', 'aws/typing']},
          })
        );
        setIsChatLoading(false);
      };

      ws.onmessage = (event) => {
        try {
          const receivedData = JSON.parse(event.data);
          if (receivedData.topic === 'aws/chat') {
            const chatContent = JSON.parse(receivedData.content);
            if (chatContent.Type === 'MESSAGE') {
              const isCustomerMessage = chatContent.ParticipantRole === 'CUSTOMER';
              const newMessage: Message = {
                content: chatContent.Content,
                sender: isCustomerMessage ? 'user' : 'bot',
                timestamp: new Date(chatContent.AbsoluteTime),
                displayName: chatContent.DisplayName,
                participantRole: chatContent.ParticipantRole,
              };

              if (!isCustomerMessage) {
                setMessages((prev) => {
                  const updated = prev.map((msg) =>
                    msg.isLoading && msg.sender === 'user' ? {...msg, isLoading: false} : msg
                  );
                  return [...updated, newMessage];
                });
                setHasPendingBotResponse(false);
                notificationSoundRef.current?.play();
              }
            } else if (chatContent.Type === 'EVENT' && chatContent.ContentType === 'application/vnd.amazonaws.connect.event.participant.joined' && chatContent.ParticipantRole === 'AGENT') {
              // Agent joined - show connection message
              const agentJoinedMessage: Message = {
                content: `You are now connected with our live agent, ${chatContent.DisplayName}.`,
                sender: 'bot',
                timestamp: new Date(chatContent.AbsoluteTime),
                displayName: 'SYSTEM_MESSAGE',
                participantRole: 'SYSTEM',
              };
              setMessages((prev) => [...prev, agentJoinedMessage]);
            } else if (chatContent.Type === 'EVENT' && chatContent.ContentType === 'application/vnd.amazonaws.connect.event.chat.ended') {
              setShouldEndChat(true);
              ws.close();
            }
          } else if (receivedData.topic === 'aws/typing') {
            const typingContent = JSON.parse(receivedData.content);
            if (typingContent.Type === 'TYPING') {
              setIsTyping(typingContent.State === 'STARTED' && typingContent.ParticipantRole !== 'CUSTOMER');
            }
          }
        } catch (err) {
          console.log('WebSocket message error:', err);
        }
      };

      ws.onerror = (err) => {
        console.log('WebSocket error:', err);
        setIsChatLoading(false);
      };

      ws.onclose = () => setIsChatLoading(false);
    } catch (err) {
      console.log('Chat initialization failed:', err);
      setIsChatLoading(false);
    }
  }, []);
  const rejectWebRTCCall = () => {
    humanWsRef.current?.send(JSON.stringify({
      action: 'sendMessage',
      session_id: humanSessionIdRef.current,
      type: 'webrtc_end',
      sender: 'customer'
    }));
    cleanupWebRTCCall();
  };

  const endWebRTCCall = () => {
    humanWsRef.current?.send(JSON.stringify({
      action: 'sendMessage',
      session_id: humanSessionIdRef.current,
      type: 'webrtc_end',
      sender: 'customer'
    }));
    cleanupWebRTCCall();
  };

  const cleanupWebRTCCall = () => {
    if (callTimeoutRef.current) {
      clearTimeout(callTimeoutRef.current);
      callTimeoutRef.current = null;
    }
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => t.stop());
      localStreamRef.current = null;
    }
    if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
      callTimerRef.current = null;
    }
    const audio = document.getElementById('webrtc-remote-audio');
    if (audio) audio.remove();
    pendingCandidatesRef.current = [];
    setCallState('idle');
    setCallDuration(0);
  };

  const startWebRTCCallTimer = () => {
    setCallDuration(0);
    if (callTimerRef.current) clearInterval(callTimerRef.current);
    callTimerRef.current = setInterval(() => setCallDuration(prev => prev + 1), 1000);
  };

  const formatCallDuration = (s: number) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  const connectHumanWs = (wsUrl: string, sessionId: string) => {
    humanSessionIdRef.current = sessionId;
    const emailVal = chatBotEmail || '';
    const nameVal = chatBotDisplayName || '';
    const email = emailVal ? `&customer_email=${encodeURIComponent(emailVal)}` : '';
    const name = nameVal ? `&customer_name=${encodeURIComponent(nameVal)}` : '';
    const ws = new WebSocket(`${wsUrl}?role=customer&session_id=${sessionId}${email}${name}`);
    humanWsRef.current = ws;
    ws.onopen = () => {
      console.log(`[WS] Customer connected at ${new Date().toLocaleString()} | Session: ${sessionId}`);
      setIsHumanMode(true);
      setMessages((prev) => [...prev, {
        content: t('common.Please wait until the Live agent receives your chat.'),
        sender: 'bot',
        timestamp: new Date(),
        isLoading: false
      }]);
    };
    ws.onmessage = (e) => {
      const data = JSON.parse(e.data);
      const _wm = data.message || data.greeting;
      if (data.type === 'message' && data.sender === 'agent') {
        setMessages((prev) => [...prev, {
          content: `${data.agent_name || 'Agent'}: ${data.message}`,
          sender: 'bot',
          timestamp: new Date(),
          isLoading: false
        }]);
      } else if (data.type === 'agent_joined') {
        setMessages((prev) => [...prev, {
          content: `Agent ${data.agent_name} is live`,
          sender: 'bot',
          timestamp: new Date(),
          isLoading: false
        }]);
      } else if (data.type === 'chat_closed' || data.type === 'agent_disconnected') {
        cleanupWebRTCCall();
        setMessages((prev) => [...prev, {
          content: t('common.The agent has ended the chat.'),
          sender: 'bot',
          timestamp: new Date(),
          isLoading: false
        }]);
        setIsHumanMode(false);
        setShouldEndChat(true);
        humanWsRef.current?.close();
        humanWsRef.current = null;
      } else if (data.type === 'waiting') {
        setMessages((prev) => [...prev, {content: _wm, sender: 'bot', timestamp: new Date(), isLoading: false}]);
      } else if (data.type === 'no_agents') {
        setMessages((prev) => [...prev, {content: _wm, sender: 'bot', timestamp: new Date(), isLoading: false}]);
        setIsHumanMode(false);
        setShouldEndChat(true);
        humanWsRef.current = null;
      } else if (data.type === 'webrtc_call' || data.type === 'webrtc_offer' || data.type === 'webrtc_answer' || data.type === 'webrtc_ice' || data.type === 'webrtc_end') {
        handleWebRTCSignal(data);
      }
    };
    ws.onclose = () => {
      cleanupWebRTCCall();
      if (isHumanMode) {
        setMessages((prev) => [...prev, {
          content: t('common.Connection to agent lost.'),
          sender: 'bot',
          timestamp: new Date(),
          isLoading: false
        }]);
        setIsHumanMode(false);
        setShouldEndChat(true);
      }
      humanWsRef.current = null;
    };
  };
  const ICE_SERVERS = [{urls: 'stun:stun.l.google.com:19302'}, {urls: 'stun:stun1.l.google.com:19302'}];
  const createPeerConnection = () => {
    const pc = new RTCPeerConnection({iceServers: ICE_SERVERS});
    pc.onicecandidate = (e) => {
      if (e.candidate && humanWsRef.current && humanSessionIdRef.current) {
        humanWsRef.current.send(JSON.stringify({
          action: 'sendMessage',
          session_id: humanSessionIdRef.current,
          type: 'webrtc_ice',
          sender: 'customer',
          candidate: JSON.stringify(e.candidate)
        }));
      }
    };
    pc.ontrack = (e) => {
      let audio = document.getElementById('webrtc-remote-audio') as HTMLAudioElement;
      if (!audio) {
        audio = document.createElement('audio');
        audio.id = 'webrtc-remote-audio';
        document.body.appendChild(audio);
      }
      audio.srcObject = e.streams[0];
      audio.autoplay = true;
      audio.play().catch(err => console.log('[WebRTC] Audio play error:', err));
    };
    pc.oniceconnectionstatechange = () => {
      if (pc.iceConnectionState === 'disconnected' || pc.iceConnectionState === 'failed') cleanupWebRTCCall();
    };
    peerConnectionRef.current = pc;
    return pc;
  };
  const handleWebRTCSignal = async (data: any) => {
    if (data.type === 'webrtc_call') {
      // Agent is calling us
      setCallState('ringing');
      if (callTimeoutRef.current) clearTimeout(callTimeoutRef.current);
      callTimeoutRef.current = setTimeout(() => {
        rejectWebRTCCall();
      }, 30000);
    } else if (data.type === 'webrtc_offer') {
      // Agent sent offer — auto-answer with our audio
      setCallState('active');
      startWebRTCCallTimer();
      try {
        const stream = await navigator.mediaDevices.getUserMedia({audio: true});
        localStreamRef.current = stream;
        const pc = createPeerConnection();
        stream.getTracks().forEach(t => pc.addTrack(t, stream));
        await pc.setRemoteDescription(new RTCSessionDescription({type: 'offer', sdp: data.sdp}));
        // Add queued ICE candidates
        for (const c of pendingCandidatesRef.current) {
          try {
            await pc.addIceCandidate(c);
          } catch (e) {
            console.log()
          }
        }
        pendingCandidatesRef.current = [];
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        humanWsRef.current?.send(JSON.stringify({
          action: 'sendMessage',
          session_id: humanSessionIdRef.current,
          type: 'webrtc_answer',
          sender: 'customer',
          sdp: answer.sdp
        }));
      } catch (e) {
        console.error('WebRTC answer failed:', e);
        cleanupWebRTCCall();
      }
    } else if (data.type === 'webrtc_answer') {
      // Agent answered our offer
      setCallState('active');
      startWebRTCCallTimer();
      if (peerConnectionRef.current) {
        await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription({
          type: 'answer',
          sdp: data.sdp
        }));
        for (const c of pendingCandidatesRef.current) {
          try {
            await peerConnectionRef.current.addIceCandidate(c);
          } catch (e) {
            console.log()
          }
        }
        pendingCandidatesRef.current = [];
      }
    } else if (data.type === 'webrtc_ice') {
      const candidate = new RTCIceCandidate(JSON.parse(data.candidate));
      if (peerConnectionRef.current && peerConnectionRef.current.remoteDescription) {
        try {
          await peerConnectionRef.current.addIceCandidate(candidate);
        } catch (e) {
          console.log()
        }
      } else {
        pendingCandidatesRef.current.push(candidate);
      }
    } else if (data.type === 'webrtc_end') {
      cleanupWebRTCCall();
    }
  };
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    const sessionIdentifier = contactId || boostSessionId;
    if (!file || !sessionIdentifier) return;

    if (file.size > 2 * 1024 * 1024) {
      alert(t('common.File size must be less than 2MB'));
      return;
    }

    setIsUploading(true);
    try {
      const uploadResponse = await fetch('https://irtnyv9rjk.execute-api.us-east-1.amazonaws.com/prod/ngteco/file-upload-url-chat', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          ...(contactId ? {contactId} : {session_id: boostSessionId}),
          fileName: file.name,
          fileSize: file.size
        })
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload URL request failed: ${uploadResponse.status}`);
      }

      const uploadData = await uploadResponse.json();

      const formData = new FormData();
      Object.entries(uploadData.fields).forEach(([key, value]) => {
        formData.append(key, value as string);
      });
      formData.append('file', file);

      try {
        await fetch(uploadData.uploadUrl, {method: 'POST', body: formData});
      } catch (error) {
        // S3 upload may succeed but throw CORS error when trying to read response
        // Continue to display URL fetch since upload likely succeeded
        console.log('S3 upload completed (CORS prevented response reading)');
      }

      const displayResponse = await fetch('https://irtnyv9rjk.execute-api.us-east-1.amazonaws.com/prod/ngteco/file-display-url-chat', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({key: uploadData.key})
      });

      if (!displayResponse.ok) {
        throw new Error(`Display URL request failed: ${displayResponse.status}`);
      }

      const displayData = await displayResponse.json();

      // Store image key for later processing when user sends message
      setPendingImageKey(uploadData.key);
      setPendingImageUrl(displayData.displayUrl);
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setIsUploading(false);
      // Clear the file input to allow selecting the same file again
      const fileInput = document.getElementById('image-upload') as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }
    }
  };
  const renameChat = async (sessionId: string, title: string) => {
    if (!title.trim()) return;
    const token = authToken;
    const email = chatBotEmail || '';
    if (!token || !email) return;
    const oldTitle = chatHistories.find(h => h.id === sessionId)?.title || '';
    setChatHistories(prev => prev.map(h => h.id === sessionId ? {...h, title: title.trim()} : h));
    setRenamingId(null);
    try {
      const res = await fetch(`${BOOST_BASE_URL}/chat-history?brand=ngteco`, {
        method: 'PATCH',
        headers: {'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'X-User-ID': email},
        body: JSON.stringify({session_id: sessionId, title: title.trim()})
      });
      if (!res.ok) setChatHistories(prev => prev.map(h => h.id === sessionId ? {...h, title: oldTitle} : h));
    } catch (e) {
      setChatHistories(prev => prev.map(h => h.id === sessionId ? {...h, title: oldTitle} : h));
    }
  };

  const deleteChat = async (sessionId: string) => {
    const token = authToken;
    const email = chatBotEmail || '';
    if (!token || !email) return;
    const backup = chatHistories;
    deletedIdsRef.current.add(sessionId);
    setChatHistories(prev => prev.filter(h => h.id !== sessionId));
    if (boostSessionId === sessionId) {
      setMessages([]);
    }
    try {
      const res = await fetch(`${BOOST_BASE_URL}/chat-history?brand=ngteco&session_id=${sessionId}`, {
        method: 'DELETE',
        headers: {'Authorization': `Bearer ${token}`, 'X-User-ID': email}
      });
      if (!res.ok) {
        deletedIdsRef.current.delete(sessionId);
        setChatHistories(backup);
      }
    } catch (e) {
      deletedIdsRef.current.delete(sessionId);
      setChatHistories(backup);
    }
  };
  const resetIdleTimer = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (idleTimeoutRef.current && !shouldEndChat) {
      idleTimerRef.current = setTimeout(() => {
        setMessages((prev) => [...prev, {
          content: t('common.Your session has timed out due to inactivity. You can continue chatting or start a new chat.'),
          sender: 'bot',
          timestamp: new Date(),
          isLoading: false
        }]);
        setShouldEndChat(true);
      }, idleTimeoutRef.current * 1000);
    }
  }, [shouldEndChat]);
  const ngtecoApiCall = async (url: string, options: RequestInit = {}) => {
    const token = authToken;
    if (!token) {
      throw new Error('No authentication token available');
    }

    const makeRequest = async (currentToken: string) => {
      return fetch(url, {
        ...options,
        mode: 'cors',
        headers: {
          ...options.headers,
          'Authorization': `Bearer ${currentToken}`
        }
      });
    };
    const refreshAccessToken = async () => {
      const storedRefreshToken = refreshToken || localStorage.getItem('ngteco_refresh_token');

      if (!storedRefreshToken) {
        console.log('NGTeco: No refresh token available');
        // handleNGTecoLogout();
        return null;
      }

      try {
        const response = await fetch("https://ai.minervaiot.com/ngteco/" + 'refresh-token', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            platform_id: 'ngtecowebsite',
            AuthFlow: 'REFRESH_TOKEN_AUTH',
            AuthParameters: {
              REFRESH_TOKEN: storedRefreshToken
            }
          })
        });

        const data = await response.json();

        if (response.ok && data.AuthenticationResult) {
          const newAccessToken = data.AuthenticationResult.AccessToken;

          setAuthToken(newAccessToken);
          setChatBotToken(newAccessToken);

          // Update stored token
          localStorage.setItem('ngteco_access_token', newAccessToken);

          // Update user info with new token
          const payload = JSON.parse(atob(newAccessToken.split('.')[1]));
          setUserInfo((prev: any) => ({
              ...prev,
              session_id: payload.session_id,
              client_type: payload.client_type
            })
          )
          ;

          console.log('NGTeco: Token refreshed successfully');
          return newAccessToken;
        } else {
          throw new Error(data.message || 'Token refresh failed');
        }
      } catch (error: any) {
        console.error('NGTeco token refresh error:', error.message);
        // handleNGTecoLogout();
        return null;
      }
    };

    let response = await makeRequest(token);

    // If 401, check if it's token expiration or usage limit
    if (response.status === 401) {
      try {
        const errorText = await response.text();

        // Don't refresh if it's usage limit exceeded
        if (errorText.includes('Token usage limit exceeded') ||
          errorText.includes('usage limit') ||
          errorText.includes('rate limit')) {
          // Return the original response to let the caller handle it
          return new Response(errorText, {status: 401});
        }

        // For other 401 errors (token expiration), try refresh
        const newToken = await refreshAccessToken();

        if (newToken) {
          response = await makeRequest(newToken);
        } else {
          throw new Error('Token refresh failed');
        }
      } catch (parseError) {
        // If we can't parse the error, assume it's token expiration and try refresh
        const newToken = await refreshAccessToken();

        if (newToken) {
          response = await makeRequest(newToken);
        } else {
          throw new Error('Token refresh failed');
        }
      }
    }

    return response;
  };

  const fetchSuggestedQuestions = useCallback(async (force = false, isRefresh = false, token: any, email: string = chatBotEmail) => {
    if (!force && suggestedQuestions.length > 0) return;
    if (!token) return;

    if (isRefresh) {
      setLoadingSuggestions(true);
    } else {
      setInitialLoadingSuggestions(true);
    }

    try {
      const response = await ngtecoApiCall('https://ai.minervaiot.com/ngteco/trending-questions', {
        method: 'GET',
        headers: {'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'X-User-ID': email}
      });
      if (response.ok) {
        const data = await response.json();
        const qaList = (data.questions || []).map((q: any) =>
          typeof q === 'string' ? {question: q, answer: ''} : q
        );
        setSuggestedQAData(qaList);
        const questions = qaList.map((q: any) => q.question);
        setSuggestedQuestions(questions);
      } else {
        console.error('NGTeco: Suggested questions failed with status:', response.status);
        // Fallback to default questions
        setSuggestedQuestions([
          "How do I create an account in the NGTeco Office software?",
          "How do you connect the NG-TC2 device to Wi-Fi?",
          "What are the key features of the NG-TC series device mentioned in the User Manual?",
          "How do I add and assign a shift schedule in the NGTeco Office software for the users?"
        ]);
      }
    } catch (error) {
      console.error('NGTeco: Failed to fetch suggested questions:', error);
      setSuggestedQuestions([
        "How do I create an account in the NGTeco Office software?",
        "How do you connect the NG-TC2 device to Wi-Fi?",
        "What are the key features of the NG-TC series device mentioned in the User Manual?",
        "How do I add and assign a shift schedule in the NGTeco Office software for the users?"
      ]);
    } finally {
      if (isRefresh) {
        setLoadingSuggestions(false);
      } else {
        setInitialLoadingSuggestions(false);
      }
    }
  }, [suggestedQuestions.length, authToken]);
  const pinChat = async (sessionId: string, pinned: boolean) => {
    const token = authToken;
    const email = chatBotEmail || '';
    if (!token || !email) return;
    setChatHistories(prev => prev.map(h => h.id === sessionId ? {...h, pinned} : h));
    try {
      const res = await fetch(`${BOOST_BASE_URL}/chat-history?brand=ngteco`, {
        method: 'PATCH',
        headers: {'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'X-User-ID': email},
        body: JSON.stringify({session_id: sessionId, pinned})
      });
      if (!res.ok) setChatHistories(prev => prev.map(h => h.id === sessionId ? {...h, pinned: !pinned} : h));
    } catch (e) {
      setChatHistories(prev => prev.map(h => h.id === sessionId ? {...h, pinned: !pinned} : h));
    }
  };
  const fetchChatHistory = async (page = 1) => {
    const token = authToken;
    const email = chatBotEmail || '';
    if (!token || !email) return;
    if (page === 1) setLoadingChatHistory(true); else setLoadingMore(true);
    try {
      const perPage = page === 1 ? 10 : 5;
      const response = await fetch(`${BOOST_BASE_URL}/chat-history?brand=ngteco&page=${page}&per_page=${perPage}`, {
        headers: {'Authorization': `Bearer ${token}`, 'X-User-ID': email}
      });
      if (response.ok) {
        const data = await response.json();
        setChatHistories(prev => {
          const newItems = data.histories || [];
          if (page === 1) return newItems;
          const existingIds = new Set(prev.map((item: any) => item.id));
          return [...prev, ...newItems.filter((item: any) => !existingIds.has(item.id))];
        });
        setChatHistoryPage(data.page);
        setChatHistoryTotal(data.total_pages);
      }
    } catch (e) {
      console.error('Failed to fetch chat history:', e);
    } finally {
      setLoadingChatHistory(false);
      setLoadingMore(false);
    }
  };
  const handleSuggestedQuestionClick = async (question: string) => {
    if (isBoostMode) {
      if (!boostSessionId) return;
      const userMessage: Message = {content: question, sender: 'user', timestamp: new Date(), isLoading: false};
      const hasUserMessage = messages.some(m => m.sender === 'user');
      setMessages((prev) => [...prev, userMessage]);
      if (!hasUserMessage && boostSessionId) {
        setChatHistories(h => h.some(item => item.id === boostSessionId) ? h : [{
          id: boostSessionId,
          title: question.slice(0, 50),
          messages: [],
          timestamp: new Date().toISOString()
        }, ...h]);
      }
      setPendingSuggestions([]);
      setHasPendingBotResponse(true);
      resetIdleTimer();
      try {
        const {
          answer,
          intent,
          suggestions,
          all_models,
          websocket_url
        } = await sendBoostMessage(question, boostSessionId);
        if (answer) setMessages((prev) => [...prev, {
          content: answer,
          sender: 'bot',
          timestamp: new Date(),
          isLoading: false
        }]);
        resetIdleTimer();
        if (intent === 'transferToHuman' && websocket_url && boostSessionId) {
          connectHumanWs(websocket_url, boostSessionId);
        } else if (intent === 'goodbye') {
          setShouldEndChat(true);
        }
        setPendingSuggestions(suggestions);
        if (Object.keys(all_models).length > 0) setAllModels(
          Object.fromEntries(Object.entries(all_models).map(([k, v]) => [k, [...v].sort((a, b) => a.localeCompare(b))]))
        );
      } catch {
        setMessages((prev) => [...prev, {
          content: t('common.Failed to get response.'),
          sender: 'bot',
          timestamp: new Date(),
          isLoading: false
        }]);
      } finally {
        setHasPendingBotResponse(false);
      }
      return;
    }
    // Don't allow if chat is loading, typing, or missing required tokens
    if (shouldEndChat || isTyping || hasPendingBotResponse || isChatLoading || !contactId || !participantToken || !wsUrl || !connectionToken) {
      return;
    }

    const userMessage: Message = {
      content: question,
      sender: 'user',
      timestamp: new Date(),
      isLoading: true,
    };

    setMessages((prev) => [...prev, userMessage]);
    setHasPendingBotResponse(true);

    // Refresh suggested questions after sending message
    // fetchSuggestedQuestions(true, false, authToken);

    try {
      // Send message without metadata for suggested questions
      await ngtecoApiCall(`https://ai.minervaiot.com/api/ngteco/send-message`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({connectionToken, content: question}),
      });

      // Send typing stopped via WebSocket
      wsRef.current?.send(JSON.stringify({topic: 'aws/typing', content: {Type: 'TYPING', State: 'STOPPED'}}));

      // Play notification sound
      notificationSoundRef.current.play().catch(() => {
        // Ignore audio play errors
      });
    } catch (error) {
      // Handle error - update message to show failure
      setMessages((prev) =>
        prev.map((msg) => msg === userMessage ? {...msg, isLoading: false, content: 'Failed to send message.'} : msg)
      );
      setHasPendingBotResponse(false);
    }
  };
  const handleStartNewChat = async () => {
    const wasBoost = isBoostMode;
    handleEndChat();
    setIsWidgetOpen(true);
    setIsMinimized(false);
    if (wasBoost) {
      setIsBoostMode(true);
      setIsChatLoading(true);
      fetchChatHistory(1);
      try {
        const res = await fetch(`${BOOST_BASE_URL}${BOOST_START_CHAT_ENDPOINT}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'X-User-ID': chatBotEmail || '',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({displayName: chatBotDisplayName || ''}),
        });
        const data = await res.json();
        setBoostSessionId(data.session_id || data.connectionToken);
        if (data.idleTimeout) {
          idleTimeoutRef.current = data.idleTimeout;
        }
        const _wm = data.message || data.greeting;
        if (_wm) setMessages([{content: _wm, sender: 'bot', timestamp: new Date(), isLoading: false}]);
      } catch {
        setMessages([{
          content: t('common.Failed to start Boost session.'),
          sender: 'bot',
          timestamp: new Date(),
          isLoading: false
        }]);
      } finally {
        setIsChatLoading(false);
      }
    }
  };
  const handleToggleWidget = () => {
    if (!isWidgetOpen) {
      setIsWidgetOpen(true);
      setIsMinimized(false);
    } else {
      setIsMinimized((prev) => !prev);
    }
  }

  useEffect(() => {

    if (!isWidgetOpen) {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      return;
    }

    if (shouldEndChat) {
      setIsTyping(false);
      setInput("");
      setHasPendingBotResponse(false);
      return;
    }

    const initChatAfterToken = async () => {
      setIsChatLoading(true);
      try {
        const res = await UserService.getChatBotToken();
        const token = res.data?.data?.access_token;
        const email = res.data?.email;
        const name = res.data?.display_name;

        if (!token) return;

        setChatBotToken(token);
        setAuthToken(token);
        setChatBotEmail(email);
        setChatBotDisplayName(name);
        setIsAuthenticated(true)

        if (!isBoostMode && !isMinimized && isWidgetOpen && !wsRef.current && !shouldEndChat) {
          initializeChat(token, email);
          fetchSuggestedQuestions(true, false, token, email);
        } else if (isBoostMode && !isMinimized && isWidgetOpen && !boostSessionId && !shouldEndChat) {
          setIsChatLoading(true);
          fetchSuggestedQuestions(true, false, token, email);
          fetchChatHistory();
          try {
            const res = await fetch(`${BOOST_BASE_URL}${BOOST_START_CHAT_ENDPOINT}`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${authToken}`,
                'X-User-ID': chatBotEmail || '',
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({displayName: chatBotDisplayName || ''}),
            });
            const data = await res.json()
            setBoostSessionId(data.session_id || data.connectionToken);
            if (data.idleTimeout) {
              idleTimeoutRef.current = data.idleTimeout;
            }
            const welcomeMsg = data.message || data.greeting;
            if (welcomeMsg) setMessages([{
              content: welcomeMsg,
              sender: 'bot',
              timestamp: new Date(),
              isLoading: false
            }]);
            resetIdleTimer();
          } catch {
            setMessages([{
              content: t('common.Connecting, pls wait.'),
              sender: 'bot',
              timestamp: new Date(),
              isLoading: false
            }]);
          } finally {
            setIsChatLoading(false)
          }
        } else if (!isWidgetOpen && wsRef.current) {
          wsRef.current.close();
          wsRef.current = null;
        } else if (isWidgetOpen && isMinimized) {
          setIsWidgetOpen(false)
        }

      } catch (err) {
        console.error("获取 token 失败", err);
      } finally {
        setIsChatLoading(false)
      }
    };

    initChatAfterToken();
  }, [isWidgetOpen, shouldEndChat, isMinimized, initializeChat, isAuthenticated, isBoostMode, boostSessionId]);
  const commonEmojis = ['😀', '😂', '😊', '😍', '😢', '😡', '😮', '😎', '🤔', '😴', '👍', '👎', '👏', '🙏', '💪', '❤️', '💯', '🔥', '✅', '❌'];

  useEffect(() => {
    chatBottomRef.current?.scrollIntoView({behavior: 'smooth'});
    setNewStartChat(true)
  }, [messages]);

  const BOOST_BASE_URL = "https://ai.minervaiot.com";
  const BOOST_START_CHAT_ENDPOINT = "/api/ngteco/start-chat";
  const BOOST_SEND_MESSAGE_ENDPOINT = "/api/ngteco/send-message";
  const sendBoostMessage = async (question: string, sessionId: string): Promise<{ answer: string; intent: string; suggestions: string[]; all_models: Record<string, string[]>; websocket_url?: string }> => {
    const email = userInfo?.email || '';
    const body = {content: question, connectionToken: sessionId, language: 'en-US'};
    const res = await fetch(`${BOOST_BASE_URL}${BOOST_SEND_MESSAGE_ENDPOINT}`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}`, 'X-User-ID': chatBotEmail},
      body: JSON.stringify(body),
    });
    const data = await res.json();
    return {
      answer: data.answer || t('common.No response received.'),
      intent: data.intent || '',
      suggestions: data.suggestions || [],
      all_models: data.all_models || {},
      websocket_url: data.websocket_url
    };
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const plainText = input.replace(/<[^>]+>/g, '').trim();

    if (isBoostMode) {
      if (!plainText || !boostSessionId) return;
      const userMessage: Message = {
        content: plainText,
        sender: 'user',
        timestamp: new Date(),
        isLoading: false,
        imageData: pendingImageUrl || undefined
      };
      const hasUserMessage = messages.some(m => m.sender === 'user');
      setMessages((prev) => [...prev, userMessage]);
      if (!hasUserMessage && boostSessionId) {
        setChatHistories(h => h.some(item => item.id === boostSessionId) ? h : [{
          id: boostSessionId,
          title: plainText.slice(0, 50),
          messages: [],
          timestamp: new Date().toISOString()
        }, ...h]);
      }
      setInput('');
      const imageKey = pendingImageKey;
      setPendingImageKey(null);
      setPendingImageUrl(null);
      setPendingSuggestions([]);
      setHasPendingBotResponse(true);
      try {
        // If already in human mode, send via WebSocket
        if (isHumanMode && humanWsRef.current?.readyState === WebSocket.OPEN) {
          humanWsRef.current.send(JSON.stringify({
            action: 'sendMessage',
            session_id: boostSessionId,
            message: plainText,
            sender: 'customer',
            timestamp: String(Date.now())
          }));
          setHasPendingBotResponse(false);
          setPendingImageKey(null);
          setPendingImageUrl(null);
          return;
        }

        // If there's a pending image, extract text and combine with user message
        let messageToSend = plainText;
        if (imageKey) {
          try {
            const extractResponse = await fetch('https://irtnyv9rjk.execute-api.us-east-1.amazonaws.com/prod/ngteco/extract-text', {
              method: 'POST',
              headers: {'Content-Type': 'application/json'},
              body: JSON.stringify({key: imageKey})
            });
            const extractData = await extractResponse.json();
            const extractedText = extractData.text || '';
            if (extractedText && extractedText !== 'NO_TEXT_FOUND') {
              messageToSend = `${plainText}\n\n[Image text]: ${extractedText}`;
            }
          } catch (e) {
            console.error('Image text extraction failed:', e);
          }
        }

        const {
          answer,
          intent,
          suggestions,
          all_models,
          websocket_url
        } = await sendBoostMessage(messageToSend, boostSessionId);
        resetIdleTimer();
        if (answer) {
          // Typewriter effect
          const msgId = Date.now();
          setMessages((prev) => [...prev, {
            content: '',
            sender: 'bot',
            timestamp: new Date(),
            isLoading: false,
            id: msgId
          } as any]);
          let i = 0;
          const speed = 2; // ms per tick
          const typeInterval = setInterval(() => {
            i += 10;
            if (i >= answer.length) {
              clearInterval(typeInterval);
              setMessages((prev) => prev.map(m => (m as any).id === msgId ? {...m, content: answer} : m));
            } else {
              setMessages((prev) => prev.map(m => (m as any).id === msgId ? {...m, content: answer.slice(0, i)} : m));
            }
          }, speed);
        }
        if (intent === 'transferToHuman' && websocket_url && boostSessionId) {
          connectHumanWs(websocket_url, boostSessionId);
        } else if (intent === 'goodbye') {
          setShouldEndChat(true);
        }
        setPendingSuggestions(suggestions);
        if (Object.keys(all_models).length > 0) setAllModels(
          Object.fromEntries(Object.entries(all_models).map(([k, v]) => [k, [...v].sort((a, b) => a.localeCompare(b))]))
        );
      } catch {
        setMessages((prev) => [...prev, {
          content: t('common.Failed to get response.'),
          sender: 'bot',
          timestamp: new Date(),
          isLoading: false
        }]);
      } finally {
        setHasPendingBotResponse(false);
      }
      return;
    }
    if (!plainText || !contactId || !participantToken || !wsUrl || !connectionToken) return;

    const userMessage: Message = {
      content: input,
      sender: 'user',
      timestamp: new Date(),
      isLoading: true,
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setHasPendingBotResponse(true);

    try {
      // await fetch('https://armaturachatbot.minervaiotstaging.com/api/ngteco/send-message', {
      await fetch('https://ai.minervaiot.com/api/ngteco/send-message', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          "Authorization": 'Bearer ' + chatBotToken,
          "X-User-ID": chatBotEmail
        },
        // body: JSON.stringify({connectionToken, content: userMessage.content}),
        body: JSON.stringify({connectionToken, content: input}),
      });

      wsRef.current?.send(JSON.stringify({
        topic: 'aws/typing',
        content: {Type: 'TYPING', State: 'STOPPED'},
      }));
    } catch (err) {
      console.log('Message failed:', err);
    }
  };

  const handleEndChat = () => {

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setMessages([]);
    setInput('');
    setIsTyping(false);
    setHasPendingBotResponse(false);
    setContactId(null);
    setParticipantToken(null);
    setParticipantToken(null);
    setConnectionToken(null);
    setWsUrl(null);
    setIsChatLoading(false);
    setShouldEndChat(false);
  };
  const handleDisconnectChat = () => {
    handleEndChat();
    setIsWidgetOpen(false);
    setIsMinimized(false);
    setIsBoostMode(true);
    setBoostSessionId(null);
  };
  const formatTime = (date: Date) => date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit'
  });

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 6,
        right: 6,
        zIndex: 50,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'end',
        gap: 2,
        flexWrap: 'nowrap',
      }}
    >
      {isWidgetOpen && (
        <Paper
          sx={{
            width: '98%',
            maxWidth: 2640,
            minWidth: 1280,
            height: '85vh',
            maxHeight: 800,
            borderRadius: '10px',
            boxShadow: 'xl',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* 顶部通栏蓝色Header */}
          <Box
            sx={{
              backgroundColor: 'primary.main',
              px: 2,
              py: 1.5,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box sx={{display: 'flex', alignItems: 'center'}}>
              <Bot style={{color: 'white', fontSize: 20}}/>
              <Typography fontWeight="bold" color="white" sx={{ml: 1, fontSize: 16}}>
                {t("common.NGTeco AI Chatbot")}
              </Typography>
            </Box>
            <Box sx={{display: 'flex', gap: 1, alignItems: 'center'}}>
              <Box>
                <IconButton
                  onClick={() => setShowUserDropdown(!showUserDropdown)}
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    backgroundColor: '#9c27b0',
                    color: 'white',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    boxShadow: 3,
                    '&:hover': {
                      backgroundColor: '#7b1fa2',
                    },
                  }}
                  title={t("common.User Profile")}
                >
                  {(chatBotDisplayName || chatBotEmail || 'U').charAt(0).toUpperCase()}
                </IconButton>
                {showUserDropdown && (
                  <Paper
                    elevation={8}
                    sx={{
                      position: 'absolute',
                      right: -8,
                      top: 12,
                      width: 320,
                      borderRadius: '12px',
                      border: '1px solid rgba(229,230,232)',
                      overflow: 'hidden',
                      marginTop: '50px',
                      zIndex: 50,
                    }}
                  >
                    {chatBotDisplayName && (
                      <>
                        <Box
                          sx={{
                            background: 'linear-gradient(to right, #2563eb, #4f46e5)',
                            px: 6,
                            py: 2,
                          }}
                        >
                          <Typography sx={{color: 'white', fontSize: 18, fontWeight: 700}}>
                            {t('common.User Profile')}
                          </Typography>
                        </Box>
                        <Box sx={{px: 6, py: 6, bgcolor: 'grey.100'}}>
                          <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                            <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 1, maxWidth: '100%'}}>
                              <Typography variant="caption"
                                          sx={{
                                            fontWeight: 600,
                                            color: 'text.secondary',
                                            whiteSpace: 'nowrap',
                                            mt: 0.5
                                          }}>
                                {t("common.Name") + " :"}
                              </Typography>
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  color: 'text.primary',
                                  wordBreak: 'break-word',
                                  overflowWrap: 'break-word'
                                }}
                              >
                                {chatBotDisplayName || 'User'}
                              </Typography>
                            </Box>
                            <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 1, maxWidth: '100%'}}>
                              <Typography variant="caption"
                                          sx={{
                                            fontWeight: 600,
                                            color: 'text.secondary',
                                            whiteSpace: 'nowrap',
                                            mt: 0.5
                                          }}>
                                {t("common.Email") + " :"}
                              </Typography>
                              <Typography
                                sx={{
                                  color: 'text.primary',
                                  wordBreak: 'break-all',
                                  overflowWrap: 'break-word'
                                }}
                              >
                                {chatBotEmail}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </>
                    )}
                  </Paper>
                )}
              </Box>
              <IconButton sx={{color: 'white', p: 0.5}} onClick={() => setIsMinimized(true)}>
                <Minus size={18}/>
              </IconButton>
              <IconButton sx={{color: 'white', p: 0.5}} onClick={() => setShowFeedbackModal(true)}>
                <X size={18}/>
              </IconButton>
            </Box>
          </Box>
          {showFeedbackModal && (
            <Dialog
              open={showFeedbackModal}
              onClose={() => {
                setShowFeedbackModal(false);
                setFeedbackResolved(null);
                setFeedbackIssues([]);
                setFeedbackText('');
                handleDisconnectChat();
              }}
              maxWidth="sm"
              PaperProps={{
                sx: {
                  width: 520,
                  borderRadius: 3,
                  position: 'relative',
                  p: 4,
                  boxShadow: 12
                }
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: 16,
                  left: 16,
                  width: 96,
                  height: 96,
                  bgcolor: 'white',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1
                }}
              >
                <img src={'/images/logo-zkbiobiotime-ngteco.png'} alt="NGTeco" style={{width: 120, height: 60, marginLeft: 30}}/>
              </Box>

              {/* 右上角关闭按钮 */}
              <IconButton
                onClick={() => {
                  setShowFeedbackModal(false);
                  setFeedbackResolved(null);
                  setFeedbackIssues([]);
                  setFeedbackText('');
                  handleDisconnectChat();
                }}
                sx={{
                  position: 'absolute',
                  top: 40,
                  right: 16,
                  color: 'grey.400',
                  '&:hover': {color: 'grey.600', bgcolor: 'grey.100'}
                }}
              >
                <CloseIcon/>
              </IconButton>

              <DialogContent sx={{mt: 10}}>
                <Typography variant="h6" align="center" fontWeight={600} sx={{mb: 3}}>
                  {t("common.Has your query been resolved?")}
                </Typography>

                {/* Yes / No 按钮 */}
                <Box sx={{display: 'flex', gap: 3, justifyContent: 'center', mb: 3}}>
                  <Button
                    variant="outlined"
                    onClick={() => setFeedbackResolved('yes')}
                    sx={{
                      px: 6,
                      py: 1,
                      fontSize: '1rem',
                      fontWeight: 500,
                      transform: 'scale(1)',
                      ...(feedbackResolved === 'yes'
                        ? {
                          bgcolor: '#2BA9DE',
                          color: '#fff',
                          borderColor: '#2BA9DE',
                          '&:hover': {bgcolor: '#2BA9DE', borderColor: '#2BA9DE', transform: 'scale(1.05)'}
                        }
                        : {
                          bgcolor: 'grey.100',
                          color: 'grey.700',
                          borderColor: 'grey.200',
                          '&:hover': {borderColor: '#2BA9DE', transform: 'scale(1.05)'}
                        })
                    }}
                  >
                    {t("att.timeSheet.Yes")}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => setFeedbackResolved('no')}
                    sx={{
                      px: 6,
                      py: 1,
                      fontSize: '1rem',
                      fontWeight: 500,
                      transform: 'scale(1)',
                      ...(feedbackResolved === 'no'
                        ? {
                          bgcolor: '#2BA9DE',
                          color: '#fff',
                          borderColor: '#2BA9DE',
                          '&:hover': {bgcolor: '#2BA9DE', borderColor: '#2BA9DE', transform: 'scale(1.05)'}
                        }
                        : {
                          bgcolor: 'grey.100',
                          color: 'grey.700',
                          borderColor: 'grey.200',
                          '&:hover': {borderColor: '#2BA9DE', transform: 'scale(1.05)'}
                        })
                    }}
                  >
                    {t("att.timeSheet.No")}
                  </Button>
                </Box>

                {feedbackResolved === 'no' && (
                  <>
                    <Typography variant="body1" align="center" color="text.secondary" sx={{mb: 2}}>
                      {t("common.Kindly advise if you have any issues.")}
                    </Typography>

                    <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2}}>
                      {[t('common.Incomplete'), t('common.Not understood'),
                        t('common.Wrong answer'), t('common.Unresolved')].map((issue) => (
                        <Button
                          key={issue}
                          variant="outlined"
                          onClick={() => {
                            setFeedbackIssues((prev) =>
                              prev.includes(issue) ? prev.filter((i) => i !== issue) : [...prev, issue]
                            );
                          }}
                          sx={{
                            py: 1,
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            transform: 'scale(1)',
                            ...(feedbackIssues.includes(issue)
                              ? {
                                bgcolor: '#2BA9DE',
                                color: '#fff',
                                borderColor: '#2BA9DE',
                                '&:hover': {bgcolor: '#2BA9DE', borderColor: '#2BA9DE', transform: 'scale(1.05)'}
                              }
                              : {
                                bgcolor: 'grey.100',
                                color: 'grey.700',
                                borderColor: 'grey.200',
                                '&:hover': {borderColor: '#2BA9DE', transform: 'scale(1.05)'}
                              })
                          }}
                        >
                          {issue}
                        </Button>
                      ))}
                    </Box>

                    <TextField
                      multiline
                      rows={3}
                      fullWidth
                      placeholder={t("common.Tell us your suggestions...")}
                      value={feedbackText}
                      onChange={(e) => setFeedbackText(e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: '#fff',
                          '& fieldset': {borderColor: '#2BA9DE'},
                          '&:hover fieldset': {borderColor: '#2BA9DE'},
                          '&.Mui-focused fieldset': {borderColor: '#2BA9DE'}
                        }
                      }}
                    />
                  </>
                )}
              </DialogContent>

              <DialogActions sx={{justifyContent: 'center', px: 4, pb: 4}}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={async () => {
                    if (!feedbackResolved) {
                      alert(t('common.Please select Yes or No before submitting'));
                      return;
                    }
                    setIsSubmittingFeedback(true);
                    try {
                      const post_data = {
                          contact_id: contactId || boostSessionId || '',
                          platform: 'ngteco',
                          resolved: feedbackResolved,
                          issues: feedbackIssues,
                          feedback_text: feedbackText
                        }
                      const response = await UserService.chatBotFeedback(post_data, authToken || chatBotToken).then((res: any) => {
                        if (res.status < 400) {
                          setContactId('');
                          enqueueSnackbar(t('common.Thank you for your feedback!'), {
                            variant: "success",
                            autoHideDuration: 1500,
                            anchorOrigin: {
                              vertical: "top",
                              horizontal: "center"
                            }
                          })
                        } else {
                          enqueueSnackbar(t('common.Failed to submit feedback'), {
                            variant: "error",
                            autoHideDuration: 1500,
                            anchorOrigin: {
                              vertical: "top",
                              horizontal: "center"
                            }
                          })
                        }
                      });
                    } catch (error) {
                      enqueueSnackbar(t('common.Failed to submit feedback'), {
                        variant: "error",
                        autoHideDuration: 1500,
                        anchorOrigin: {
                          vertical: "top",
                          horizontal: "center"
                        }
                      })
                    } finally {
                      setIsSubmittingFeedback(false);
                      setShowFeedbackModal(false);
                      setFeedbackResolved(null);
                      setFeedbackIssues([]);
                      setFeedbackText('');
                      handleDisconnectChat();
                    }
                  }}
                  disabled={isSubmittingFeedback}
                  sx={{
                    bgcolor: '#2BA9DE',
                    '&:hover': {bgcolor: '#2BA9DE'},
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 500
                  }}
                >
                  {isSubmittingFeedback ? (
                    <>
                      <CircularProgress size={16} color="inherit" sx={{mr: 1}}/>
                      {t("common.Submitting...")}
                    </>
                  ) : (
                    t("common.submit")
                  )}
                </Button>
              </DialogActions>
            </Dialog>
          )}

          {/* Header下方横向三栏容器 */}
          <Box sx={{flex: 1, display: 'flex', flexDirection: 'row', overflow: 'hidden'}}>
            {/* 左栏：Chat History 280px */}
            <Box
              sx={{
                flex: '0 0 280px',
                maxWidth: '280px',
                borderRight: '1px solid #e0e0e0',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: '#fafbfc',
                boxSizing: 'border-box',
                overflow: 'hidden',
              }}
            >
              <Box sx={{p: 2, borderBottom: '1px solid #e0e0e0'}}>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  sx={{
                    display: 'block',
                    mb: 2,
                    pb: 1,
                    borderBottom: '1px solid',
                    borderColor: 'grey.200',
                    textAlign: 'center',
                    fontSize: "16px",
                  }}
                >
                  {t("common.Chat History")}
                </Typography>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<AddIcon fontSize="small"/>}
                  onClick={handleStartNewChat}
                  sx={{
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    fontSize: "16px",
                    '&:hover': {bgcolor: 'grey.100'}
                  }}
                >
                  {t("common.New chat")}
                </Button>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  px: 1.5,
                  pt: 0.5,
                  pb: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.25,
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                  '@keyframes fadeIn': {
                    from: {opacity: 0},
                    to: {opacity: 1}
                  },
                  '@keyframes pulse': {
                    '0%, 100%': {opacity: 1},
                    '50%': {opacity: .5}
                  }
                }}
              >
                {loadingChatHistory ? (
                  Array.from({length: 10}).map((_, i) => (
                    <Box
                      key={i}
                      sx={{
                        animation: 'pulse 2s infinite',
                        px: 1.5,
                        py: 0.5
                      }}
                    >
                      <Box sx={{height: 12, bgcolor: 'grey.200', borderRadius: 1, width: '100%'}}/>
                    </Box>
                  ))
                ) : chatHistories.length > 0 ? (() => {
                  const unique = chatHistories.filter((h, i, arr) => arr.findIndex(x => x.id === h.id) === i);
                  const pinned = unique.filter(h => h.pinned);
                  const unpinned = unique.filter(h => !h.pinned);

                  const renderItem = (h: any, index: number, isPinned: boolean) => {
                    const isActive = h.id === boostSessionId;
                    const isItemLoading = loadingSession === h.id;

                    return (
                      <Box
                        key={`${h.id}-${index}`}
                        title={h.title}
                        onClick={async () => {
                          if (renamingId === h.id) return;
                          setLoadingSession(h.id);
                          setMessages([]);
                          setBoostSessionId(h.id);
                          setShouldEndChat(true);
                          const token = authToken;
                          const email = chatBotEmail || '';
                          try {
                            const res = await fetch(`${BOOST_BASE_URL}/chat-history?brand=ngteco&session_id=${h.id}`, {
                              headers: {'Authorization': `Bearer ${token}`, 'X-User-ID': email}
                            });
                            if (res.ok) {
                              const data = await res.json();
                              setMessages(data.messages.map((m: any) => ({
                                ...m,
                                timestamp: new Date(m.timestamp),
                                isLoading: false
                              })));
                            }
                          } catch (e) {
                            console.error('Failed to load session:', e);
                          } finally {
                            setLoadingSession(null);
                          }
                        }}
                        sx={{
                          position: 'relative',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.25,
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 2,
                          fontSize: '0.75rem',
                          cursor: 'pointer',
                          animation: isItemLoading ? 'pulse 2s infinite' : !isPinned ? `fadeIn 0.4s ease-out backwards ${index * 80}ms` : 'none',
                          bgcolor: isActive ? 'grey.200' : 'transparent',
                          fontWeight: isActive ? 500 : 400,
                          color: isActive ? 'grey.900' : 'grey.700',
                          '&:hover': {bgcolor: isActive ? 'grey.200' : 'grey.200'},
                          '&:hover .actionIcon': {opacity: 1}
                        }}
                      >
                        {renamingId === h.id ? (
                          <TextField
                            autoFocus
                            fullWidth
                            size="small"
                            value={renameValue}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => setRenameValue(e.target.value)}
                            onBlur={() => renameChat(h.id, renameValue)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') renameChat(h.id, renameValue);
                              if (e.key === 'Escape') setRenamingId(null);
                            }}
                            sx={{
                              flex: 1,
                              '& .MuiOutlinedInput-root': {
                                fontSize: '14px',
                                bgcolor: '#fff',
                                py: 0
                              }
                            }}
                          />
                        ) : (
                          <Typography noWrap
                                      sx={{flex: 1, fontSize: '14px', fontWeight: 400}}>{h.title}</Typography>
                        )}

                        {/* Pin Button */}
                        <IconButton
                          className="actionIcon"
                          size="small"
                          sx={{opacity: 0, flexShrink: 0, p: 0.5}}
                          title={isPinned ? 'Unpin' : 'Pin'}
                          onClick={(e) => {
                            e.stopPropagation();
                            pinChat(h.id, !isPinned);
                          }}
                        >
                          <Box
                            component="svg"
                            width={14}
                            height={14}
                            fill="currentColor"
                            viewBox="0 0 24 24"
                            sx={{transform: 'rotate(45deg)', color: isPinned ? 'grey.700' : 'grey.500'}}
                          >
                            <path
                              d="M14 4v5c0 1.12.37 2.16 1 3H9c.63-.84 1-1.88 1-3V4h4m3-2H7c-.55 0-1 .45-1 1s.45 1 1 1h1v5c0 1.66-1.34 3-3 3v2h5.97v7l1 1 1-1v-7H19v-2c-1.66 0-3-1.34-3-3V4h1c.55 0 1-.45 1-1s-.45-1-1-1z"/>
                          </Box>
                        </IconButton>

                        {/* More Button */}
                        <IconButton
                          className="actionIcon"
                          size="small"
                          sx={{opacity: 0, flexShrink: 0, p: 0.5}}
                          title="More"
                          onClick={(e) => {
                            e.stopPropagation();
                            setMenuOpenId(menuOpenId === h.id ? null : h.id);
                          }}
                        >
                          <Box
                            component="svg"
                            width={14}
                            height={14}
                            fill="currentColor"
                            viewBox="0 0 24 24"
                            sx={{color: 'grey.500'}}
                          >
                            <circle cx="12" cy="5" r="2"/>
                            <circle cx="12" cy="12" r="2"/>
                            <circle cx="12" cy="19" r="2"/>
                          </Box>
                        </IconButton>

                        {/* Dropdown Menu */}
                        {menuOpenId === h.id && (
                          <Paper
                            elevation={4}
                            sx={{
                              position: 'absolute',
                              right: 0,
                              top: 32,
                              zIndex: 50,
                              width: 112,
                              py: 1
                            }}
                            onMouseLeave={() => setMenuOpenId(null)}
                          >
                            <Button
                              fullWidth
                              size="small"
                              sx={{
                                justifyContent: 'flex-start',
                                fontSize: '0.75rem',
                                px: 1.5,
                                py: 0.75,
                                gap: 1,
                                textTransform: 'none',
                                '&:hover': {bgcolor: 'grey.100'}
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setMenuOpenId(null);
                                setRenamingId(h.id);
                                setRenameValue(h.title);
                              }}
                            >
                              <Box component="svg" width={12} height={12} fill="none" stroke="currentColor"
                                   strokeWidth={2} viewBox="0 0 24 24">
                                <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                                <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                              </Box>
                              {t("common.Rename")}
                            </Button>
                            <Button
                              fullWidth
                              size="small"
                              sx={{
                                justifyContent: 'flex-start',
                                fontSize: '0.75rem',
                                px: 1.5,
                                py: 0.75,
                                gap: 1,
                                textTransform: 'none',
                                color: 'error.main',
                                '&:hover': {bgcolor: 'error.light'}
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setMenuOpenId(null);
                                deleteChat(h.id);
                              }}
                            >
                              <Box component="svg" width={12} height={12} fill="none" stroke="currentColor"
                                   strokeWidth={2} viewBox="0 0 24 24">
                                <path d="M3 6h18M8 6V4h8v2M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6"/>
                              </Box>
                              {t("common.delete")}
                            </Button>
                          </Paper>
                        )}
                      </Box>
                    );
                  };

                  return (
                    <>
                      {pinned.length > 0 && (
                        <>
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: '9px',
                              textTransform: 'uppercase',
                              fontWeight: 600,
                              color: 'grey.400',
                              px: 1.5,
                              pt: 0.5
                            }}
                          >
                            {t("common.Pinned")}
                          </Typography>
                          {pinned.map((h, index) => renderItem(h, index, true))}
                          {unpinned.length > 0 && (
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: '9px',
                                textTransform: 'uppercase',
                                fontWeight: 600,
                                color: 'grey.400',
                                px: 1.5,
                                pt: 1
                              }}
                            >
                              {t("common.Recent")}
                            </Typography>
                          )}
                        </>
                      )}
                      {unpinned.map((h, index) => renderItem(h, index, false))}
                    </>
                  );
                })() : (
                  <Typography sx={{px: 1.5, py: 1, fontSize: '0.75rem', color: 'grey.400'}}>
                    {t("common.No history yet")}
                  </Typography>
                )}

                {/* Load More Button */}
                {chatHistoryTotal > chatHistoryPage && (
                  <Button
                    fullWidth
                    size="small"
                    disabled={loadingMore}
                    onClick={() => fetchChatHistory(chatHistoryPage + 1)}
                    sx={{
                      fontSize: '0.75rem',
                      color: 'grey.500',
                      py: 2,
                      textTransform: 'none',
                      '&:hover': {color: 'grey.700'}
                    }}
                  >
                    {loadingMore ? t('common.Loading...') : t('common.Load more...')}
                  </Button>
                )}
              </Box>
            </Box>

            {/* 中间：聊天窗口 600px */}
            <Box
              sx={{flex: '0 0 600px', display: 'flex', flexDirection: 'column', overflow: 'hidden'}}>
              <Box
                sx={{flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: 'white'}}>
                <Box sx={{
                  flex: 1,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  p: 2,
                  boxSizing: 'border-box',
                  maxWidth: '100%'
                }}>
                  {isChatLoading && messages.length === 0 && (
                    <Box sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      backgroundColor: 'white',
                      zIndex: 10
                    }}>
                      <CircularProgress sx={{mb: 4}}/>
                      <Typography color="text.secondary">{t("common.Loading...")}</Typography>
                    </Box>
                  )}
                  {messages.map((msg, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'start',
                        mt: 2,
                        gap: 2, ...(msg.sender === 'user' && {flexDirection: 'row-reverse'})
                      }}
                    >
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          ...(msg.sender === 'user' && {backgroundColor: 'primary.main', color: 'white'}),
                          ...(msg.sender === 'bot' && {backgroundColor: '#f5f5f5', color: 'text.secondary'}),
                        }}
                      >
                        {msg.sender === 'user' ? <User size={18}/> : <Bot size={18}/>}
                      </Box>
                      <Box sx={{maxWidth: '75%', position: 'relative'}}>
                        <Box sx={{
                          position: 'absolute',
                          top: -22,
                          left: 0,
                          right: 0,
                          display: 'flex',
                          justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start'
                        }}>
                          <Typography variant="caption" color="text.secondary"
                                      sx={{whiteSpace: 'nowrap'}}>{formatTime(msg.timestamp)}</Typography>
                        </Box>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 1.5,
                            borderRadius: '10px',
                            fontSize: 14,
                            ...(msg.sender === 'user' && {
                              backgroundColor: 'primary.main',
                              color: 'white',
                              borderBottomRightRadius: 0
                            }),
                            ...(msg.sender === 'bot' && {backgroundColor: '#f5f5f5', borderBottomLeftRadius: 0}),
                          }}
                        >
                          <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
                            {/* 如果存在图片，优先渲染图片 */}
                            {msg.imageData && (
                              <Box
                                component="img"
                                src={msg.imageData}
                                alt="chat upload"
                                sx={{
                                  maxWidth: '100%',
                                  maxHeight: 300,
                                  borderRadius: 1,
                                  objectFit: 'contain'
                                }}
                              />
                            )}
                            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end'}}>
                              <Box sx={{flexGrow: 1, mr: 1}}>
                                {msg.content ? <ReactMarkdown>{msg.content}</ReactMarkdown> : null}
                              </Box>
                              <CopyToClipboard text={msg.sender === 'user' ? removeMarkdown(msg.content) : msg.content}>
                                <Tooltip title={t("common.Copy message")}>
                                  <IconButton size="small" sx={{
                                    color: msg.sender === 'user' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.5)',
                                    p: 0.5
                                  }}>
                                    <Copy size={14}/>
                                  </IconButton>
                                </Tooltip>
                              </CopyToClipboard>
                            </Box>
                          </Box>
                        </Paper>
                      </Box>
                    </Box>
                  ))}
                  {(isTyping || hasPendingBotResponse) && !shouldEndChat && (
                    <Box sx={{display: 'flex', alignItems: 'start', gap: 2}}>
                      <Box sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '10px',
                        backgroundColor: '#f5f5f5',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Bot style={{color: '#757575'}}/>
                      </Box>
                      <LoadingContainer><Dot delay={150}/><Dot delay={300}/><Dot delay={450}/></LoadingContainer>
                    </Box>
                  )}
                  {!shouldEndChat && <Box ref={chatBottomRef}/>}
                </Box>
                <Divider/>
                {!shouldEndChat && (
                  <Box sx={{position: 'relative'}}>
                    {pendingImageUrl && (
                      <Box sx={{pt: 2, display: 'flex', alignItems: 'center', gap: 2}}>
                        <Box
                          component="img"
                          src={pendingImageUrl}
                          alt="pending"
                          sx={{
                            width: 96,
                            height: 96,
                            objectFit: 'cover',
                            borderRadius: 1,
                            border: '1px solid #e0e0e0'
                          }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {t("common.Image attached")}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setPendingImageKey(null);
                            setPendingImageUrl(null);
                          }}
                          sx={{
                            color: 'grey.400',
                            fontSize: '0.875rem',
                            '&:hover': {color: 'error.main'}
                          }}
                        >
                          ✕
                        </IconButton>
                      </Box>
                    )}
                    <Box sx={{display: 'flex', gap: 1, alignItems: 'center', width: '100%'}}>
                      <IconButton type="button" onClick={() => setShowEmojiPicker(prev => !prev)}
                                  sx={{width: 32, height: 32, flexShrink: 0, '&:hover': {bgcolor: '#f3f4f6'}}}>
                        <Smile size={16}/>
                      </IconButton>
                      <Box component="label" sx={{width: 32, height: 32, flexShrink: 0, cursor: 'pointer'}}>
                        {/* 原生隐藏文件输入 */}
                        <input
                          type="file"
                          accept="image/*"
                          hidden
                          id="image-upload"
                          onChange={handleFileUpload}
                          disabled={isUploading}
                        />
                        {/* MUI 按钮样式层 */}
                        <IconButton
                          size="small"
                          sx={{
                            width: '100%',
                            height: '100%',
                            '&:hover': {bgcolor: 'grey.100'}
                          }}
                          component="span"
                        >
                          {isUploading ? (
                            <Loader size={16} style={{animation: 'spin 1s linear infinite'}}/>
                          ) : (
                            <Image size={16} color="#154396"/>
                          )}
                        </IconButton>
                      </Box>
                      <Box
                        onClick={() => handleSuggestedQuestionClick("Transfer to a live agent")}
                        sx={{
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                          color: '#2563eb',
                          fontWeight: 700,
                          display: 'flex',
                          alignItems: 'center',
                          marginLeft: 'auto',
                          mr: '30px'
                        }}
                      >
                        <Box component="img" src="/images/call-center-service.svg" sx={{width: 16, height: 16, mr: 1}}/>
                        {t("common.Transfer to a live agent")}
                      </Box>
                    </Box>
                    {showEmojiPicker && (
                      <Box sx={{
                        position: 'absolute',
                        bottom: 60,
                        left: 20,
                        bgcolor: 'white',
                        borderRadius: '12px',
                        p: 2,
                        zIndex: 9999,
                        width: 320
                      }}>
                        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2}}>
                          <Typography
                            sx={{fontSize: '1rem', fontWeight: 500, color: '#374151'}}>{t("common.Emojis")}</Typography>
                          <IconButton size="small" onClick={() => setShowEmojiPicker(false)}><X size={24}/></IconButton>
                        </Box>
                        <Grid container spacing={1}>
                          {commonEmojis.map((emoji, index) => (
                            <Grid item xs={1.5} key={index}>
                              <IconButton onClick={() => {
                                setInput(p => p + emoji);
                                setShowEmojiPicker(false);
                              }}>{emoji}</IconButton>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    )}
                  </Box>
                )}
                {!shouldEndChat && (
                  <Box sx={{p: 2}}>
                    <form onSubmit={handleSubmit} style={{display: 'flex', gap: 2, alignItems: 'center'}}>
                      <TextField fullWidth variant="outlined" size="small" value={input}
                                 disabled={(isTyping || hasPendingBotResponse) && !shouldEndChat}
                                 onChange={(e) => setInput(e.target.value)}
                                 placeholder={t("common.Type your message...")}
                                 sx={{'& .MuiOutlinedInput-root': {borderRadius: '20px', backgroundColor: '#fafafa'}}}/>
                      <IconButton type="submit"
                                  sx={{backgroundColor: '#19409E', color: 'white', borderRadius: '50%', p: 1.5}}
                                  disabled={isTyping || hasPendingBotResponse || !input.trim() || isChatLoading}><Send
                        size={18}/></IconButton>

                    </form>
                  </Box>
                )}
                {shouldEndChat && messages.length > 0 && (
                  <Box
                    sx={{
                      borderTop: 1,
                      borderColor: 'grey.200',
                      p: 3,
                      display: 'flex',
                      justifyContent: 'center',
                      gap: 3
                    }}
                  >
                    <Button
                      variant="contained"
                      onClick={() => {
                        setShouldEndChat(false);
                        resetIdleTimer();
                      }}
                      sx={{
                        bgcolor: '#6ab04c',
                        color: '#fff',
                        fontWeight: 700,
                        py: 1.25,
                        px: 3,
                        boxShadow: 2,
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          inset: 0,
                          bgcolor: '#7ab84a',
                          transform: 'translateY(100%)',
                          transition: 'transform 0.5s ease-out'
                        },
                        '&:hover::before': {
                          transform: 'translateY(0)'
                        },
                        '&:hover': {
                          bgcolor: '#6ab04c' // 覆盖MUI默认hover变色，由伪层实现hover效果
                        }
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          position: 'relative',
                          zIndex: 1,
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <MessageSquare size={16} style={{marginRight: 8}}/>
                        {t("common.Continue Chat")}
                      </Box>
                    </Button>

                    <Button
                      variant="contained"
                      onClick={handleStartNewChat}
                      sx={{
                        bgcolor: '#6ab04c',
                        color: '#fff',
                        fontWeight: 700,
                        py: 1.25,
                        px: 3,
                        boxShadow: 2,
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          inset: 0,
                          bgcolor: '#7ab84a',
                          transform: 'translateY(100%)',
                          transition: 'transform 0.5s ease-out'
                        },
                        '&:hover::before': {
                          transform: 'translateY(0)'
                        },
                        '&:hover': {
                          bgcolor: '#6ab04c'
                        }
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          position: 'relative',
                          zIndex: 1,
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <PlusCircle size={16} style={{marginRight: 8}}/>
                        {t("common.Start New Chat")}
                      </Box>
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>

            {/* 右栏：推荐&联系方式 380px */}
            <Box
              sx={{
                flex: '0 0 380px',
                minWidth: '380px',
                borderLeft: '1px solid #e0e0e0',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: '#fafbfc',
                overflow: 'hidden',
              }}
            >
              <Box sx={{borderBottom: 1, borderColor: 'divider', backgroundColor: 'white'}}>
                <Tabs value={activeTab} onChange={(_, val) => setActiveTab(val)} variant="fullWidth">
                  <Tab value="suggested" label={t("common.Suggested Questions")}/>
                  <Tab value="contact" label={t("common.Contact Details")}/>
                </Tabs>
              </Box>

              {/* Tab 内容区域 */}
              <Box sx={{
                flex: 1,
                overflowY: 'auto',
                overflowX: 'hidden', // 禁止横向滚动/溢出撑开宽度
                p: 2,
                minHeight: 400,
                boxSizing: 'border-box',
                maxWidth: '100%'
              }}>
                {/* Suggested Questions 面板 */}
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 1.5}}>
                  <IconButton
                    onClick={() => fetchSuggestedQuestions(true, true, authToken, chatBotEmail)}
                    disabled={loadingSuggestions}
                    size="small"
                    sx={{
                      color: '#2563eb',
                      '&:hover': {
                        backgroundColor: 'rgba(37, 99, 235, 0.04)',
                      },
                      '&.Mui-disabled': {
                        color: '#9ca3af',
                      },
                    }}
                    title="Get new suggestions"
                  >
                    {loadingSuggestions ? (
                      <CircularProgress size={16} sx={{color: '#2563eb'}}/>
                    ) : (
                      <Refresh sx={{fontSize: 16}}/>
                    )}
                  </IconButton>
                </Box>
                {activeTab === "suggested" && (
                  (loadingSuggestions || initialLoadingSuggestions) ? (
                    // 加载中状态 - MUI
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      py: 4
                    }}>
                      <CircularProgress
                        size={16}
                        sx={{
                          color: '#19409E',
                          borderWidth: 2
                        }}
                      />
                      <Typography sx={{ml: 1, fontSize: '0.875rem', color: 'text.secondary'}}>
                        {t("common.Loading suggestions...")}
                      </Typography>
                    </Box>
                  ) : selectedQA ? (<Box
                    sx={{
                      animation: 'fadeIn 0.3s ease',
                      '@keyframes fadeIn': {
                        from: {opacity: 0},
                        to: {opacity: 1}
                      }
                    }}
                  >
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => setSelectedQA(null)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        fontSize: '0.75rem',
                        color: '#6ab04c',
                        mb: 3,
                        fontWeight: 500,
                        '&:hover': {color: '#6a9e3a'}
                      }}
                    >
                      ← {t("common.Back")}
                    </Button>

                    <Box
                      sx={{
                        borderLeft: '2px solid #6ab04c',
                        pl: 2,
                        mb: 3
                      }}
                    >
                      <Typography variant="caption" sx={{fontWeight: 600, color: 'grey.800'}}>
                        💡 {selectedQA.question}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        fontSize: '0.75rem',
                        color: 'grey.700',
                        lineHeight: 1.6,
                        bgcolor: 'green.50',
                        borderRadius: 2,
                        p: 3,
                        border: '1px solid',
                        borderColor: 'green.100',
                        maxWidth: '100%'
                      }}
                    >
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          h1: ({children}) => <Typography variant="subtitle1"
                                                          sx={{fontWeight: 700, my: 1}}>{children}</Typography>,
                          h2: ({children}) => <Typography variant="caption" sx={{
                            fontWeight: 700,
                            my: 1,
                            display: 'block'
                          }}>{children}</Typography>,
                          h3: ({children}) => <Typography variant="caption" sx={{
                            fontWeight: 600,
                            my: 1,
                            display: 'block'
                          }}>{children}</Typography>,
                          ul: ({children}) => <ul
                            style={{listStyle: 'disc', paddingLeft: 16, margin: '4px 0'}}>{children}</ul>,
                          ol: ({children}) => <ol
                            style={{listStyle: 'decimal', paddingLeft: 16, margin: '4px 0'}}>{children}</ol>,
                          li: ({children}) => <li style={{marginBottom: 2}}>{children}</li>,
                          p: ({children}) => <Typography variant="caption"
                                                         sx={{my: 1, display: 'block'}}>{children}</Typography>,
                          code: ({children}) => <code style={{
                            background: '#dcfce7',
                            padding: '2px 4px',
                            borderRadius: 4,
                            fontSize: '0.875rem'
                          }}>{children}</code>,
                          strong: ({children}) => <strong style={{fontWeight: 600}}>{children}</strong>,
                        }}
                      >
                        {selectedQA.answer}
                      </ReactMarkdown>
                    </Box>
                  </Box>) : suggestedQuestions.length > 0 ? (
                    <Box sx={{mt: 1}}>
                      {suggestedQuestions.slice(0, 8).map((question, index) => (
                        <Typography
                          key={index}
                          // onClick={() => handleSuggestedQuestionClick(question)}
                          onClick={() => setSelectedQA(suggestedQAData[index] || {question, answer: ''})}
                          sx={{
                            fontSize: '0.875rem',
                            color: '#19409E',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            pl: 1,
                            py: 0.5,
                            borderLeft: '2px solid #bfdbfe',
                            '&:hover': {
                              color: 'black',
                              borderLeftColor: '#60a5fa'
                            }
                          }}
                        >
                          • {question}
                        </Typography>
                      ))}
                    </Box>

                  ) : (
                    // 无数据状态 - MUI
                    <Box sx={{
                      textAlign: 'center',
                      py: 4,
                      color: 'text.secondary',
                      fontSize: '0.875rem'
                    }}>
                      {t("common.No suggestions available")}
                    </Box>
                  )
                )}

                {/* Contact Details 面板 */}
                {activeTab !== "suggested" && (
                  <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                    {/* Call Us 模块 */}
                    <Paper variant="outlined" sx={{p: 2, borderRadius: 2}}>
                      <Box sx={{display: 'flex', alignItems: 'center', mb: 2}}>
                        <Phone size={16} color="#d32f2f"/>
                        <Typography variant="h6" sx={{
                          ml: 1,
                          fontWeight: 500,
                          fontSize: "12px !important"
                        }}>{t("common.Call Us")}</Typography>
                      </Box>
                      <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.5}}>
                        {[
                          {country: 'US:', number: '+1 800-864-3814 (toll free)'},
                          {country: 'Canada:', number: '+1 438-300-8567'},
                          {country: 'Mexico:', number: '+52 800-649-0344 (toll free)'},
                          {country: 'UK:', number: '+44 808-812-6646 (toll free)'},
                          {country: 'Spain:', number: '+34 919-942-161'},
                          {country: 'Germany:', number: '+49 322-1224-6737'},
                          {country: 'Japan:', number: '+81 800-300-8197 (toll free)'},
                          {country: 'Malaysia:', number: '+60 3-9212-1725'},
                        ].map((item, i) => (
                          <Box key={i} sx={{display: 'flex', alignItems: 'center'}}>
                            <Typography
                              sx={{
                                fontWeight: 500,
                                minWidth: '110px', // 固定宽度，保证所有国家名右对齐
                                textAlign: 'left',
                                fontSize: '12px',
                                color: 'text.primary',
                              }}
                            >
                              {item.country}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: '12px',
                                color: 'text.primary',
                                fontFamily: 'monospace', // 等宽字体，保证数字对齐
                              }}
                            >
                              {item.number}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Paper>

                    {/* Email Support 模块 */}
                    <Paper variant="outlined" sx={{p: 2, borderRadius: 2}}>
                      <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                        <Box sx={{display: 'flex', alignItems: 'center'}}>
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: '50%',
                              backgroundColor: '#e8f5e9',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mr: 1,
                            }}
                          >
                            <Mail size={16} color="#2e7d32"/>
                          </Box>
                          <Typography variant="h6" sx={{
                            fontWeight: 500,
                            fontSize: "12px !important"
                          }}>{t("common.Email Support")}</Typography>
                        </Box>
                        <Typography variant="body1"
                                    sx={{fontWeight: 500, fontSize: "16px!important"}}>ngtime@ngteco.com</Typography>
                      </Box>
                    </Paper>

                    {/* Get Support 模块 */}
                    <Paper variant="outlined" sx={{p: 2, borderRadius: 2}}>
                      <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                        <Box sx={{display: 'flex', alignItems: 'center'}}>
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: '50%',
                              backgroundColor: '#f3e5f5',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mr: 1,
                            }}
                          >
                            <Headphones size={16} color="#9c27b0"/>
                          </Box>
                          <Typography variant="h6"
                                      sx={{fontWeight: 500, fontSize: "12px"}}>{t("common.Get Support")}</Typography>
                        </Box>
                        <Typography sx={{color: '#9c27b0', cursor: 'pointer', fontSize: '12px'}}><SupportMenuItem
                          type={2}/></Typography>
                      </Box>
                    </Paper>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Paper>
      )}


      {!isWidgetOpen && (
        <Fab
          onClick={() => handleToggleWidget()}
          sx={{
            p: 3,
            borderRadius: 'full',
            backgroundColor: 'primary.main',
            color: 'white',
            right: "30px",
            bottom: "10vh",
            boxShadow: 'none',
            '&:hover': {backgroundColor: 'primary.dark'},
          }}
        >
          <TextsmsOutlinedIcon/>
        </Fab>
      )}
    </Box>
  );
}


export default ChatBot;
